import type { TFunction } from 'i18next';
import type { TenantDetail } from '@up2d8/contracts';
import { formatDate, formatTime, uiLocale } from '../../shared/format';

/**
 * Datums zoals het platformbeheer ze toont (P1, P2): kort, en hoe dichter bij vandaag hoe
 * preciezer. "vandaag 13:52", "gisteren 17:40", "ma 14/09", "02/08", "11/2025".
 */
export function wanneer(value: string | null | undefined, t: TFunction, now = new Date()): string {
  if (!value) return '—';
  const datum = new Date(value);
  if (Number.isNaN(datum.getTime())) return '—';

  const dag = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dagen = Math.round((dag(now) - dag(datum)) / 86_400_000);
  const dagMaand = formatDate(datum, { day: '2-digit', month: '2-digit' });

  if (dagen === 0) return t('beheer.platform.when.today', { time: formatTime(datum) });
  if (dagen === 1) return t('beheer.platform.when.yesterday', { time: formatTime(datum) });
  if (dagen > 1 && dagen < 7) {
    const weekdag = new Intl.DateTimeFormat(uiLocale(), { weekday: 'short' }).format(datum);
    return `${weekdag.replace(/\.$/, '')} ${dagMaand}`;
  }
  if (datum.getFullYear() === now.getFullYear()) return dagMaand;
  return maandJaar(value);
}

/** "04/2026": wanneer een bedrijf aangemaakt is, zonder dag. */
export function maandJaar(value: string | null | undefined): string {
  if (!value) return '—';
  return formatDate(value, { month: '2-digit', year: 'numeric' }) || '—';
}

/** Is er vandaag iets gebeurd in dit bedrijf? */
export function vandaagActief(tenant: TenantDetail, now = new Date()): boolean {
  if (!tenant.lastActivityAt) return false;
  return new Date(tenant.lastActivityAt).toDateString() === now.toDateString();
}

/**
 * De soort van een bedrijf in één woord: Gedeeld, Persoonlijk, Archief - of Klant, voor een gewoon
 * bedrijf (`kind` is dan `null`).
 */
export function soortLabel(kind: TenantDetail['kind'], t: TFunction): string {
  return kind ? t(`beheer.platform.kinds.${kind}`) : t('beheer.platform.kinds.customer');
}
